import {
  startTransition,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { validateWhitelistedStudyFilesMeta } from '@/services/whitelistedStudyFilesApi';
import { getActiveWhitelistFilenameSet } from '@/services/activeWhitelistCsv';
import * as validationPreferences from '@/utils/validationPreferences';
import {
  FileMetadata,
  FileUploadStrategy,
  FileWithValidation,
  MetaValidationMode,
  UseFileValidationReturn,
  ValidationRule,
} from '../types';

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export const useFileValidation = ({
  metaValidationMode = 'server',
}: {
  metaValidationMode?: MetaValidationMode;
} = {}): UseFileValidationReturn => {
  const [files, setFiles] = useState<FileWithValidation[]>([]);
  const [isAddingFiles, setIsAddingFiles] = useState(false);
  const [addingFilesProgress, setAddingFilesProgress] = useState<{
    scanned: number;
    total: number;
  } | null>(null);
  const [metaValidationProgress, setMetaValidationProgress] = useState({
    isRunning: false,
    isPaused: false,
    totalFiles: 0,
    completedFiles: 0,
    remainingFiles: 0,
    percent: 0,
    allowedCount: 0,
    notAllowedCount: 0,
    existsCount: 0,
    totalBatches: 0,
    completedBatches: 0,
    failedBatches: 0,
    runningBatches: 0,
    batches: [],
    config: null,
    lastRunWasCancelled: false,
  });
  const abortControllerRef = useRef<AbortController | null>(null);
  const pauseControlRef = useRef({
    paused: false,
    resumeResolvers: [] as Array<() => void>,
  });
  const addRunIdRef = useRef(0);
  const fileIdCounterRef = useRef(0);

  const pauseMetaValidation = useCallback(() => {
    pauseControlRef.current.paused = true;
    setMetaValidationProgress(prev =>
      prev.isPaused ? prev : { ...prev, isPaused: true }
    );
  }, []);

  const resumeMetaValidation = useCallback(() => {
    pauseControlRef.current.paused = false;
    const resolvers = pauseControlRef.current.resumeResolvers.splice(0);
    resolvers.forEach(r => {
      try {
        r();
      } catch {
        // ignore
      }
    });
    setMetaValidationProgress(prev =>
      prev.isPaused ? { ...prev, isPaused: false } : prev
    );
  }, []);

  // Basic validation rules
  const validationRules: ValidationRule[] = useMemo(
    () => [
      {
        id: 'file-size',
        name: 'File Size Check',
        description: 'Check if file size is within acceptable limits',
        validate: async (file: File) => {
          const maxSize = 100 * 1024 * 1024; // 100MB
          if (file.size > maxSize) {
            return {
              isValid: false,
              message: 'File size exceeds 100MB limit',
              errors: ['File too large'],
            };
          }
          return { isValid: true };
        },
      },
      {
        id: 'file-extension',
        name: 'File Extension Check',
        description: 'Check if file has valid extension',
        validate: async (file: File) => {
          const allowedExtensions = ['.txt', '.csv', '.json', '.xml', '.dcm'];
          const extension = '.' + file.name.split('.').pop()?.toLowerCase();

          if (!allowedExtensions.includes(extension)) {
            return {
              isValid: false,
              message: `File type ${extension} not supported`,
              errors: ['Invalid file type'],
            };
          }
          return { isValid: true };
        },
      },
    ],
    []
  );

  // Add files with duplicate detection
  const addFiles = useCallback((newFiles: File[] | FileList) => {
    const runId = (addRunIdRef.current += 1);
    setIsAddingFiles(true);

    // IMPORTANT: if caller clears an <input type="file"> value right after onChange,
    // some browsers may clear the underlying FileList. Take a snapshot synchronously.
    const snapshotFiles: File[] = (() => {
      if (Array.isArray(newFiles)) return newFiles;
      const list = newFiles as FileList;
      const len = list.length ?? 0;
      const out: File[] = [];
      out.length = 0;
      for (let i = 0; i < len; i += 1) {
        const f = list.item(i);
        if (f) out.push(f);
      }
      return out;
    })();

    const total = snapshotFiles.length;
    setAddingFilesProgress({ scanned: 0, total });

    // For very large selections, prioritize total throughput over incremental UI updates.
    // Incremental progress updates/yields can significantly increase total time (quadratic
    // re-computation costs elsewhere). We still yield once to let "Preparing…" paint.
    const LARGE_TOTAL_THRESHOLD = 20000;
    const useFastPath = total >= LARGE_TOTAL_THRESHOLD;

    // Yield to let the UI show a loading state before heavy work.
    setTimeout(() => {
      void (async () => {
        try {
          const prepared: FileWithValidation[] = [];
          let scanned = 0;

          let lastYieldAt =
            typeof performance !== 'undefined' && typeof performance.now === 'function'
              ? performance.now()
              : Date.now();

          for (let i = 0; i < snapshotFiles.length; i += 1) {
            if (addRunIdRef.current !== runId) return; // cancelled/superseded
            const file = snapshotFiles[i];
            scanned += 1;

            // Exclude system files and files without extension
            if (!file?.name || file.name.startsWith('.')) continue;
            const lastDot = file.name.lastIndexOf('.');
            if (!(lastDot > 0 && lastDot < file.name.length - 1)) continue;

            const idSuffix = (fileIdCounterRef.current += 1);
            prepared.push({
              file,
              validationStatus: {
                id: `${file.name}-${file.size}-${file.lastModified}-${idSuffix}`,
                status: 'pending' as const,
              },
              webkitRelativePath: (file as File).webkitRelativePath,
            });

            if (!useFastPath) {
              // Update progress and yield occasionally to keep UI responsive (small/medium lists).
              if (scanned % 5000 === 0) {
                setAddingFilesProgress(prev =>
                  prev ? { ...prev, scanned } : { scanned, total }
                );
              }

              const now =
                typeof performance !== 'undefined' && typeof performance.now === 'function'
                  ? performance.now()
                  : Date.now();
              if (now - lastYieldAt >= 16) {
                await sleep(0);
                lastYieldAt =
                  typeof performance !== 'undefined' &&
                  typeof performance.now === 'function'
                    ? performance.now()
                    : Date.now();
              }
            }
          }

          // Final progress update (for small lists). For fast path we avoid extra state work.
          if (!useFastPath) {
            setAddingFilesProgress({ scanned, total });
          }

          // Apply all at once to avoid repeated expensive re-computations (duplicates/statistics).
          // There may still be a short pause for the final render on very large lists.
          if (!useFastPath) {
            await sleep(0);
          }
          setFiles(prev => prev.concat(prepared));
        } finally {
          // Only clear loading state if this is the latest run.
          if (addRunIdRef.current === runId) {
            setIsAddingFiles(false);
            setAddingFilesProgress(null);
          }
        }
      })();
    }, 0);
  }, []);

  // Remove file
  const removeFile = useCallback((fileToRemove: FileWithValidation) => {
    setFiles(prev =>
      prev.filter(
        f => f.validationStatus.id !== fileToRemove.validationStatus.id
      )
    );
  }, []);

  // Validate single file
  const validateFile = useCallback(
    async (file: FileWithValidation) => {
      // Update status to validating
      setFiles(prev =>
        prev.map(f =>
          f === file
            ? {
                ...f,
                validationStatus: {
                  ...f.validationStatus,
                  status: 'validating',
                },
              }
            : f
        )
      );

      try {
        let isValid = true;
        const errors: string[] = [];
        const warnings: string[] = [];

        // Run all validation rules
        for (const rule of validationRules) {
          try {
            const result = await rule.validate(file.file);
            if (!result.isValid) {
              isValid = false;
              if (result.errors) errors.push(...result.errors);
              if (result.warnings) warnings.push(...result.warnings);
            }
          } catch (error) {
            isValid = false;
            errors.push(`Validation rule ${rule.name} failed: ${error}`);
          }
        }

        // Update file with validation results
        setFiles(prev =>
          prev.map(f =>
            f === file
              ? {
                  ...f,
                  validationStatus: {
                    ...f.validationStatus,
                    status: isValid ? 'valid' : 'invalid',
                    message: isValid ? 'File is valid' : errors.join(', '),
                    errors: errors.length > 0 ? errors : undefined,
                    warnings: warnings.length > 0 ? warnings : undefined,
                    validatedAt: new Date(),
                  },
                }
              : f
          )
        );
      } catch (error) {
        setFiles(prev =>
          prev.map(f =>
            f === file
              ? {
                  ...f,
                  validationStatus: {
                    ...f.validationStatus,
                    status: 'error',
                    message: `Validation failed: ${error}`,
                    errors: [`Validation error: ${error}`],
                    validatedAt: new Date(),
                  },
                }
              : f
          )
        );
      }
    },
    [validationRules]
  );

  // Retry validation
  const retryValidation = useCallback(
    async (file: FileWithValidation) => {
      await validateFile(file);
    },
    [validateFile]
  );

  // Clear all files
  const clearAllFiles = useCallback(() => {
    // Cancel any ongoing add operation.
    addRunIdRef.current += 1;
    setIsAddingFiles(false);
    setAddingFilesProgress(null);
    setFiles([]);
    pauseControlRef.current.paused = false;
    pauseControlRef.current.resumeResolvers.splice(0).forEach(r => {
      try {
        r();
      } catch {
        // ignore
      }
    });
    setMetaValidationProgress({
      isRunning: false,
      isPaused: false,
      totalFiles: 0,
      completedFiles: 0,
      remainingFiles: 0,
      percent: 0,
      allowedCount: 0,
      notAllowedCount: 0,
      existsCount: 0,
      totalBatches: 0,
      completedBatches: 0,
      failedBatches: 0,
      runningBatches: 0,
      batches: [],
      config: null,
      lastRunWasCancelled: false,
    });
  }, []);

  const cancelMetaValidation = useCallback(() => {
    abortControllerRef.current?.abort();
    // Ensure any paused loop is unblocked.
    pauseControlRef.current.paused = false;
    pauseControlRef.current.resumeResolvers.splice(0).forEach(r => {
      try {
        r();
      } catch {
        // ignore
      }
    });
    setMetaValidationProgress(prev =>
      prev.isPaused ? { ...prev, isPaused: false } : prev
    );
  }, []);

  const runMetaValidation = useCallback(
    async (mode: 'all' | 'failed-only') => {
      const nonDuplicateFiles = files.filter(f => !f.isDuplicate);
      if (nonDuplicateFiles.length === 0) return;

      // Cancel any previous run
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      pauseControlRef.current.paused = false;
      setMetaValidationProgress(prev =>
        prev.isPaused ? { ...prev, isPaused: false } : prev
      );

      const config = {
        batchSize: validationPreferences.getValidationBatchSize(),
        maxConcurrent: validationPreferences.getValidationMaxConcurrent(),
        launchIntervalMs:
          validationPreferences.getValidationLaunchIntervalMs?.() ??
          validationPreferences.VALIDATION_LAUNCH_INTERVAL_MS_DEFAULT ??
          250,
      };

      const nonDuplicateFileNames = nonDuplicateFiles.map(f => f.file.name);
      const nonDuplicateFileNameSet = new Set(nonDuplicateFileNames);

      // Update status to server-validating (defer to avoid blocking UI for large lists)
      startTransition(() => {
        setFiles(prev =>
          prev.map(f =>
            nonDuplicateFileNameSet.has(f.file.name)
              ? {
                  ...f,
                  validationStatus: {
                    ...f.validationStatus,
                    status: 'server-validating',
                  },
                }
              : f
          )
        );
      });

      const nowIso = new Date().toISOString();
      const fileMetadata: FileMetadata[] = nonDuplicateFiles.map(f => ({
        filename: f.file.name,
        size: f.file.size,
        content_type: f.file.type || 'application/octet-stream',
        created_at: nowIso,
        updated_at: nowIso,
      }));

      const allBatches: FileMetadata[][] = [];
      for (let i = 0; i < fileMetadata.length; i += config.batchSize) {
        allBatches.push(fileMetadata.slice(i, i + config.batchSize));
      }

      const priorFailedBatchIndexSet =
        mode === 'failed-only'
          ? new Set(
              metaValidationProgress.batches
                .filter(b => b.status === 'error')
                .map(b => b.index)
            )
          : null;

      const selectedBatchIndices =
        priorFailedBatchIndexSet === null
          ? allBatches.map((_, idx) => idx)
          : allBatches
              .map((_, idx) => idx)
              .filter(idx => priorFailedBatchIndexSet.has(idx));

      const batchesForRun = selectedBatchIndices.map(idx => ({
        index: idx,
        metadata: allBatches[idx],
      }));

      const initialBatchesState = allBatches.map((batch, idx) => {
        const fromFileIndex = idx * config.batchSize;
        const toFileIndex = Math.min(fileMetadata.length, fromFileIndex + batch.length);
        const shouldBeInRun =
          priorFailedBatchIndexSet === null || priorFailedBatchIndexSet.has(idx);

        const previous = metaValidationProgress.batches.find(b => b.index === idx);
        const status: 'pending' | 'processing' | 'completed' | 'error' | 'cancelled' =
          shouldBeInRun ? 'pending' : previous?.status || 'pending';

        return {
          id: `meta-batch-${idx}`,
          index: idx,
          fromFileIndex,
          toFileIndex,
          fileCount: batch.length,
          status,
          error: shouldBeInRun ? undefined : previous?.error,
        };
      });

      setMetaValidationProgress({
        isRunning: true,
        isPaused: false,
        totalFiles: fileMetadata.length,
        completedFiles: 0,
        remainingFiles: fileMetadata.length,
        percent: 0,
        allowedCount: 0,
        notAllowedCount: 0,
        existsCount: 0,
        totalBatches: allBatches.length,
        completedBatches: 0,
        failedBatches: 0,
        runningBatches: 0,
        batches: initialBatchesState,
        config,
        lastRunWasCancelled: false,
      });

      const concurrency = Math.max(1, Math.floor(config.maxConcurrent || 1));
      let nextIndex = 0;
      let active = 0;

      await new Promise<void>(resolve => {
        const schedule = () => {
          if (abortController.signal.aborted) {
            setMetaValidationProgress(prev => ({
              ...prev,
              isRunning: false,
              isPaused: false,
              runningBatches: 0,
              lastRunWasCancelled: true,
              batches: prev.batches.map(b =>
                b.status === 'pending' || b.status === 'processing'
                  ? { ...b, status: 'cancelled' }
                  : b
              ),
            }));
            resolve();
            return;
          }

          while (active < concurrency && nextIndex < batchesForRun.length) {
            const job = batchesForRun[nextIndex];
            nextIndex += 1;
            active += 1;

            setMetaValidationProgress(prev => ({
              ...prev,
              runningBatches: prev.runningBatches + 1,
              batches: prev.batches.map(b =>
                b.index === job.index ? { ...b, status: 'processing' } : b
              ),
            }));

            void (async () => {
              if (config.launchIntervalMs > 0) {
                await sleep(config.launchIntervalMs);
              }

              const batch = job.metadata;
              const batchFileNames = batch.map(m => m.filename);
              const batchFileNameSet = new Set(batchFileNames);

              try {
                const serverValidationResult =
                  await validateWhitelistedStudyFilesMeta(batch, {
                    signal: abortController.signal,
                  });

                setFiles(prev =>
                  prev.map(f => {
                    if (!batchFileNameSet.has(f.file.name)) return f;

                    const serverResult = serverValidationResult.find(
                      sr => sr.filename === f.file.name
                    );

                    if (serverResult) {
                      const isAllowed = !serverResult.error;
                      const newStatus = isAllowed ? 'server-valid' : 'server-invalid';
                      const newMessage = serverResult.error || '';

                      return {
                        ...f,
                        validationStatus: {
                          ...f.validationStatus,
                          status: newStatus,
                          message: newMessage,
                          serverValidation: {
                            isAllowed,
                            exists: false,
                            error: serverResult.error,
                          },
                        },
                        uploadStrategy: 'skip' as const,
                      };
                    }

                    return {
                      ...f,
                      validationStatus: {
                        ...f.validationStatus,
                        status: 'server-valid' as const,
                        serverValidation: {
                          isAllowed: true,
                          exists: false,
                          error: undefined,
                        },
                      },
                    };
                  })
                );

                setMetaValidationProgress(prev => {
                  const completedBatches = prev.completedBatches + 1;
                  const completedFiles = Math.min(
                    prev.totalFiles,
                    prev.completedFiles + batch.length
                  );
                  const remainingFiles = Math.max(0, prev.totalFiles - completedFiles);
                  const percent =
                    prev.totalFiles > 0
                      ? Math.round((completedFiles / prev.totalFiles) * 100)
                      : 0;

                  // For meta validation, "exists" is not determined here (kept as 0).
                  const batchAllowed = serverValidationResult.filter(r => !r.error).length;
                  const batchNotAllowed = serverValidationResult.filter(r => !!r.error).length;

                  return {
                    ...prev,
                    completedBatches,
                    completedFiles,
                    remainingFiles,
                    percent,
                    allowedCount: (prev.allowedCount ?? 0) + batchAllowed,
                    notAllowedCount: (prev.notAllowedCount ?? 0) + batchNotAllowed,
                    existsCount: prev.existsCount ?? 0,
                    batches: prev.batches.map(b =>
                      b.index === job.index
                        ? { ...b, status: 'completed', error: undefined }
                        : b
                    ),
                  };
                });
              } catch (error) {
                const isAbort =
                  error instanceof DOMException && error.name === 'AbortError';
                const errorMessage = isAbort
                  ? 'Cancelled'
                  : error instanceof Error
                    ? error.message
                    : String(error);

                if (!isAbort) {
                  setFiles(prev =>
                    prev.map(f =>
                      batchFileNameSet.has(f.file.name)
                        ? {
                            ...f,
                            validationStatus: {
                              ...f.validationStatus,
                              status: 'error',
                              message: `Server validation failed: ${errorMessage}`,
                              errors: [`Server validation error: ${errorMessage}`],
                            },
                          }
                        : f
                    )
                  );
                }

                setMetaValidationProgress(prev => {
                  const completedBatches = prev.completedBatches + 1;
                  const failedBatches = isAbort ? prev.failedBatches : prev.failedBatches + 1;
                  const completedFiles = Math.min(
                    prev.totalFiles,
                    prev.completedFiles + batch.length
                  );
                  const remainingFiles = Math.max(0, prev.totalFiles - completedFiles);
                  const percent =
                    prev.totalFiles > 0
                      ? Math.round((completedFiles / prev.totalFiles) * 100)
                      : 0;
                  return {
                    ...prev,
                    completedBatches,
                    failedBatches,
                    completedFiles,
                    remainingFiles,
                    percent,
                    batches: prev.batches.map(b =>
                      b.index === job.index
                        ? {
                            ...b,
                            status: isAbort ? 'cancelled' : 'error',
                            error: isAbort ? undefined : errorMessage,
                          }
                        : b
                    ),
                  };
                });
              } finally {
                active -= 1;
                setMetaValidationProgress(prev => ({
                  ...prev,
                  runningBatches: Math.max(0, prev.runningBatches - 1),
                }));

                if (nextIndex >= batchesForRun.length && active === 0) {
                  setMetaValidationProgress(prev => ({
                    ...prev,
                    isRunning: false,
                  }));
                  resolve();
                } else {
                  schedule();
                }
              }
            })();
          }
        };

        schedule();
      });
    },
    [files, metaValidationProgress.batches]
  );

  const runClientWhitelistValidation = useCallback(
    async (_mode: 'all' | 'failed-only') => {
      const nonDuplicateFiles = files.filter(f => !f.isDuplicate);
      if (nonDuplicateFiles.length === 0) return;

      // Cancel any previous run
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      pauseControlRef.current.paused = false;
      setMetaValidationProgress(prev =>
        prev.isPaused ? { ...prev, isPaused: false } : prev
      );

      const config = {
        batchSize: validationPreferences.getValidationBatchSize(),
        maxConcurrent: Math.max(
          1,
          Math.floor(validationPreferences.getValidationMaxConcurrent() || 1)
        ),
        launchIntervalMs:
          validationPreferences.getValidationLaunchIntervalMs?.() ??
          validationPreferences.VALIDATION_LAUNCH_INTERVAL_MS_DEFAULT ??
          250,
      };

      const nonDuplicateFileNames = nonDuplicateFiles.map(f => f.file.name);
      const nonDuplicateFileNameSet = new Set(nonDuplicateFileNames);

      // Update status to server-validating
      setFiles(prev =>
        prev.map(f =>
          nonDuplicateFileNameSet.has(f.file.name)
            ? {
                ...f,
                validationStatus: {
                  ...f.validationStatus,
                  status: 'server-validating',
                },
              }
            : f
        )
      );

      const allBatches: string[][] = [];
      for (let i = 0; i < nonDuplicateFileNames.length; i += config.batchSize) {
        allBatches.push(nonDuplicateFileNames.slice(i, i + config.batchSize));
      }

      const initialBatchesState = allBatches.map((batch, idx) => {
        const fromFileIndex = idx * config.batchSize;
        const toFileIndex = Math.min(
          nonDuplicateFileNames.length,
          fromFileIndex + batch.length
        );
        return {
          id: `meta-batch-${idx}`,
          index: idx,
          fromFileIndex,
          toFileIndex,
          fileCount: batch.length,
          status: 'pending' as const,
          error: undefined as string | undefined,
        };
      });

      setMetaValidationProgress({
        isRunning: true,
        isPaused: false,
        totalFiles: nonDuplicateFileNames.length,
        completedFiles: 0,
        remainingFiles: nonDuplicateFileNames.length,
        percent: 0,
        allowedCount: 0,
        notAllowedCount: 0,
        existsCount: 0,
        totalBatches: allBatches.length,
        completedBatches: 0,
        failedBatches: 0,
        runningBatches: 0,
        batches: initialBatchesState,
        config,
        lastRunWasCancelled: false,
      });

      const isAbortError = (error: unknown): boolean => {
        if (abortController.signal.aborted) return true;
        if (error instanceof DOMException && error.name === 'AbortError') return true;
        if (error && typeof error === 'object' && 'name' in error) {
          const name = String((error as { name?: unknown }).name);
          if (name === 'CanceledError' || name === 'AbortError') return true;
        }
        return false;
      };

      const cancelProgress = () => {
        setMetaValidationProgress(prev => ({
          ...prev,
          isRunning: false,
          isPaused: false,
          runningBatches: 0,
          lastRunWasCancelled: true,
          batches: prev.batches.map(b =>
            b.status === 'pending' || b.status === 'processing'
              ? { ...b, status: 'cancelled' as const }
              : b
          ),
        }));
      };

      let activeWhitelist: Awaited<ReturnType<typeof getActiveWhitelistFilenameSet>> =
        null;

      const waitWhilePaused = async (): Promise<void> => {
        while (pauseControlRef.current.paused && !abortController.signal.aborted) {
          await Promise.race([
            new Promise<void>(resolve => {
              pauseControlRef.current.resumeResolvers.push(resolve);
            }),
            new Promise<void>(resolve => {
              const onAbort = () => {
                abortController.signal.removeEventListener('abort', onAbort);
                resolve();
              };
              abortController.signal.addEventListener('abort', onAbort, {
                once: true,
              });
            }),
          ]);
        }
      };

      try {
        await waitWhilePaused();
        activeWhitelist = await getActiveWhitelistFilenameSet({
          signal: abortController.signal,
        });
      } catch (error) {
        if (isAbortError(error)) {
          cancelProgress();
          return;
        }

        const message = error instanceof Error ? error.message : String(error);
        setFiles(prev =>
          prev.map(f =>
            nonDuplicateFileNameSet.has(f.file.name)
              ? {
                  ...f,
                  validationStatus: {
                    ...f.validationStatus,
                    status: 'error',
                    message: `Whitelist download failed: ${message}`,
                    errors: [`Whitelist download error: ${message}`],
                  },
                }
              : f
          )
        );
        setMetaValidationProgress(prev => ({
          ...prev,
          isRunning: false,
          runningBatches: 0,
          completedBatches: prev.totalBatches,
          completedFiles: prev.totalFiles,
          remainingFiles: 0,
          percent: 100,
          allowedCount: prev.allowedCount ?? 0,
          notAllowedCount: prev.notAllowedCount ?? 0,
          existsCount: prev.existsCount ?? 0,
          failedBatches: prev.totalBatches || 1,
          batches: prev.batches.map(b => ({
            ...b,
            status: 'error' as const,
            error: message,
          })),
        }));
        return;
      }

      if (!activeWhitelist) {
        const message = 'No active whitelist found';
        setFiles(prev =>
          prev.map(f =>
            nonDuplicateFileNameSet.has(f.file.name)
              ? {
                  ...f,
                  validationStatus: {
                    ...f.validationStatus,
                    status: 'server-invalid',
                    message,
                    serverValidation: {
                      isAllowed: false,
                      exists: false,
                      error: message,
                    },
                  },
                  uploadStrategy: 'skip' as const,
                }
              : f
          )
        );
        setMetaValidationProgress(prev => ({
          ...prev,
          isRunning: false,
          runningBatches: 0,
          completedBatches: prev.totalBatches,
          completedFiles: prev.totalFiles,
          remainingFiles: 0,
          percent: 100,
          allowedCount: 0,
          notAllowedCount: prev.totalFiles,
          existsCount: 0,
          failedBatches: prev.totalBatches || 1,
          batches: prev.batches.map(b => ({
            ...b,
            status: 'error' as const,
            error: message,
          })),
        }));
        return;
      }

      const allowedSet = activeWhitelist.filenames;
      const allowedByFilename = new Map<string, boolean>();

      let lastYieldAt =
        typeof performance !== 'undefined' && typeof performance.now === 'function'
          ? performance.now()
          : Date.now();

      const maybeYield = async () => {
        const now =
          typeof performance !== 'undefined' && typeof performance.now === 'function'
            ? performance.now()
            : Date.now();
        if (now - lastYieldAt >= 16) {
          await sleep(0);
          lastYieldAt =
            typeof performance !== 'undefined' && typeof performance.now === 'function'
              ? performance.now()
              : Date.now();
        }
      };

      for (let batchIndex = 0; batchIndex < allBatches.length; batchIndex += 1) {
        if (abortController.signal.aborted) {
          cancelProgress();
          return;
        }

        await waitWhilePaused();
        if (abortController.signal.aborted) {
          cancelProgress();
          return;
        }

        const batchFileNames = allBatches[batchIndex];

        setMetaValidationProgress(prev => ({
          ...prev,
          runningBatches: 1,
          batches: prev.batches.map(b =>
            b.index === batchIndex ? { ...b, status: 'processing' as const } : b
          ),
        }));

        let batchAllowed = 0;
        let batchNotAllowed = 0;
        for (const filename of batchFileNames) {
          const isAllowed = allowedSet.has(filename);
          allowedByFilename.set(filename, isAllowed);
          if (isAllowed) batchAllowed += 1;
          else batchNotAllowed += 1;
        }

        setMetaValidationProgress(prev => {
          const completedBatches = prev.completedBatches + 1;
          const completedFiles = Math.min(
            prev.totalFiles,
            prev.completedFiles + batchFileNames.length
          );
          const remainingFiles = Math.max(0, prev.totalFiles - completedFiles);
          const percent =
            prev.totalFiles > 0
              ? Math.round((completedFiles / prev.totalFiles) * 100)
              : 0;
          return {
            ...prev,
            runningBatches: 0,
            completedBatches,
            completedFiles,
            remainingFiles,
            percent,
            allowedCount: (prev.allowedCount ?? 0) + batchAllowed,
            notAllowedCount: (prev.notAllowedCount ?? 0) + batchNotAllowed,
            existsCount: prev.existsCount ?? 0,
            batches: prev.batches.map(b =>
              b.index === batchIndex
                ? { ...b, status: 'completed' as const, error: undefined }
                : b
            ),
          };
        });

        if (config.launchIntervalMs > 0) {
          await sleep(config.launchIntervalMs);
        }

        // Keep UI responsive during large validations
        await maybeYield();
      }

      // Apply results in a bounded number of state updates (single pass).
      setFiles(prev =>
        prev.map(f => {
          if (!nonDuplicateFileNameSet.has(f.file.name)) return f;

          const isAllowed = allowedByFilename.get(f.file.name);
          if (isAllowed === undefined) return f;

          if (isAllowed) {
            return {
              ...f,
              validationStatus: {
                ...f.validationStatus,
                status: 'server-valid',
                message: undefined,
                serverValidation: {
                  isAllowed: true,
                  exists: false,
                  error: undefined,
                },
              },
            };
          }

          const message = 'Filename is not in the active whitelist';
          return {
            ...f,
            validationStatus: {
              ...f.validationStatus,
              status: 'server-invalid',
              message,
              serverValidation: {
                isAllowed: false,
                exists: false,
                error: message,
              },
            },
            uploadStrategy: 'skip' as const,
          };
        })
      );

      setMetaValidationProgress(prev => ({
        ...prev,
        isRunning: false,
        isPaused: false,
        runningBatches: 0,
      }));
    },
    [files]
  );

  // Metadata validation (server or client)
  const validateFilesMeta = useCallback(async () => {
    if (metaValidationMode === 'client') {
      await runClientWhitelistValidation('all');
      return;
    }
    await runMetaValidation('all');
  }, [metaValidationMode, runClientWhitelistValidation, runMetaValidation]);

  const retryFailedMetaValidation = useCallback(async () => {
    if (metaValidationMode === 'client') {
      // Client validation does not have per-batch network failures; rerun full validation.
      await runClientWhitelistValidation('all');
      return;
    }
    await runMetaValidation('failed-only');
  }, [metaValidationMode, runClientWhitelistValidation, runMetaValidation]);

  // Set upload strategy for conflicting files
  const setUploadStrategy = useCallback(
    (file: FileWithValidation, strategy: FileUploadStrategy) => {
      setFiles(prev =>
        prev.map(f =>
          f.validationStatus.id === file.validationStatus.id
            ? { ...f, uploadStrategy: strategy }
            : f
        )
      );
    },
    []
  );

  // Get files ready for upload
  const getFilesReadyForUpload = useCallback(() => {
    return files.filter(f => {
      if (f.validationStatus.status !== 'server-valid') return false;
      if (!f.validationStatus.serverValidation?.exists) return true;
      return f.uploadStrategy === 'overwrite';
    });
  }, [files]);

  // Add duplicate detection logic
  const filesWithDuplicateStatus = useMemo(() => {
    const nameCount: Record<string, number> = {};

    // Count file names
    files.forEach(file => {
      const name = file.file.name;
      nameCount[name] = (nameCount[name] || 0) + 1;
    });

    // Mark duplicates
    return files.map(file => {
      const isDuplicate = nameCount[file.file.name] > 1;
      return {
        ...file,
        isDuplicate,
        duplicateMessage: isDuplicate
          ? `${nameCount[file.file.name]} files with this name`
          : undefined,
      };
    });
  }, [files]);

  return {
    files: filesWithDuplicateStatus,
    addFiles,
    isAddingFiles,
    addingFilesProgress,
    removeFile,
    retryValidation,
    clearAllFiles,
    validateFilesMeta,
    metaValidationProgress,
    cancelMetaValidation,
    pauseMetaValidation,
    resumeMetaValidation,
    retryFailedMetaValidation,
    setUploadStrategy,
    getFilesReadyForUpload,
  };
};
